/**
 * Standardized error hierarchy for API operations
 * Provides type-safe error handling across the application
 */

/**
 * Base API error class
 */
export class ApiError extends Error {
  public readonly name: string = 'ApiError';
  
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
    public readonly details?: unknown
  ) {
    super(message);
    
    // Ensure proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * Create error from response object
   */
  static fromResponse(response: Response, message?: string): ApiError {
    return new ApiError(
      message || `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      response.status.toString()
    );
  }

  /**
   * Check if error is retryable
   */
  get isRetryable(): boolean {
    return (
      this.status === undefined || // Network errors
      this.status >= 500 || // Server errors
      this.status === 408 || // Request timeout
      this.status === 429    // Rate limit
    );
  }

  /**
   * Get user-friendly error message
   */
  get userMessage(): string {
    switch (this.code) {
      case 'NETWORK_ERROR':
        return 'Unable to connect to the server. Please check your internet connection.';
      case 'AUTH_REQUIRED':
        return 'Please sign in to continue.';
      case 'FORBIDDEN':
        return 'You do not have permission to access this resource.';
      case 'NOT_FOUND':
        return 'The requested resource was not found.';
      case 'VALIDATION_ERROR':
        return this.message;
      default:
        return 'Something went wrong. Please try again.';
    }
  }
}

/**
 * Authentication and authorization errors
 */
export class AuthenticationError extends ApiError {
  public readonly name: string = 'AuthenticationError';
  
  constructor(message = 'Authentication required') {
    super(message, 401, 'AUTH_REQUIRED');
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }

  get isRetryable(): boolean {
    return false; // Auth errors should not be retried automatically
  }
}

/**
 * Network and connectivity errors
 */
export class NetworkError extends ApiError {
  public readonly name: string = 'NetworkError';
  
  constructor(message = 'Network connection failed') {
    super(message, undefined, 'NETWORK_ERROR');
    Object.setPrototypeOf(this, NetworkError.prototype);
  }

  get isRetryable(): boolean {
    return true; // Network errors can be retried
  }
}

/**
 * Validation and bad request errors
 */
export class ValidationError extends ApiError {
  public readonly name: string = 'ValidationError';
  
  constructor(message: string, details?: unknown) {
    super(message, 400, 'VALIDATION_ERROR', details);
    Object.setPrototypeOf(this, ValidationError.prototype);
  }

  get isRetryable(): boolean {
    return false; // Validation errors should not be retried
  }
}

/**
 * Server errors
 */
export class ServerError extends ApiError {
  public readonly name: string = 'ServerError';
  
  constructor(message = 'Internal server error', status = 500) {
    super(message, status, 'SERVER_ERROR');
    Object.setPrototypeOf(this, ServerError.prototype);
  }

  get isRetryable(): boolean {
    return true; // Server errors can be retried
  }
}

/**
 * Upload-specific errors
 */
export class UploadApiError extends ApiError {
  public readonly name: string = 'UploadApiError';
  
  constructor(
    message: string,
    status: number = 500,
    public readonly uploadDetails?: {
      failedFiles?: string[];
      partialSuccess?: boolean;
      totalFiles?: number;
      processedFiles?: number;
    }
  ) {
    super(message, status, 'UPLOAD_ERROR');
    Object.setPrototypeOf(this, UploadApiError.prototype);
  }

  get isRetryable(): boolean {
    // Don't retry validation errors (file size, type, etc.)
    if (this.status === 400 || this.status === 413 || this.status === 422) {
      return false;
    }
    // Retry server errors and network issues
    return this.status === undefined || this.status >= 500 || this.status === 408 || this.status === 429;
  }

  get userMessage(): string {
    switch (this.status) {
      case 413:
        return 'One or more files are too large. Please choose smaller files.';
      case 415:
        return 'One or more files are not supported. Please check the file types.';
      case 400:
        return this.message || 'Invalid file upload request.';
      case 429:
        return 'Too many uploads. Please wait a moment and try again.';
      case 507:
        return 'Not enough storage space available.';
      default:
        return this.message || 'Failed to upload files. Please try again.';
    }
  }
}

/**
 * Type guard functions for error checking
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isAuthenticationError(error: unknown): error is AuthenticationError {
  return error instanceof AuthenticationError;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

export function isServerError(error: unknown): error is ServerError {
  return error instanceof ServerError;
}

export function isUploadApiError(error: unknown): error is UploadApiError {
  return error instanceof UploadApiError;
}

/**
 * Error handling utilities
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.userMessage;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
}

export function shouldRetry(error: unknown): boolean {
  if (isApiError(error)) {
    return error.isRetryable;
  }
  
  return false; // Don't retry unknown errors
}
